import React, { useState } from "react";
import { DatePicker, Table } from "antd";
import moment from "moment";
import {
  FaArrowRight,
  FaCalendarAlt,
  FaUser,
  FaBuilding,
  FaDollarSign,
  FaArrowUp,
  FaArrowDown,
  FaClock,
  FaEye,
} from "react-icons/fa";
import { MdAccountBalance } from "react-icons/md";
import { IoMdAlert } from "react-icons/io";
import { useHistory } from "react-router-dom";
import "./styles.css";

const getStatusStyle = (status) => {
  const statusLower = status?.toLowerCase() || "";
  return (
    {
      paid: "statusPaid",
      completed: "statusCompleted",
      arrived: "statusArrived",
      departed: "statusDeparted",
      pending: "statusPending",
      scheduled: "statusScheduled",
      in_transit: "statusInTransit",
      loading: "statusLoading",
      end: "statusEnd",
      overdue: "statusOverdue",
    }[statusLower] || ""
  );
};

const StatCard = ({ title, value, icon: Icon, color, bgColor, onClick }) => (
  <div className="statCard" onClick={onClick}>
    <div className="statCardContent">
      <div className="statInfo">
        <p className="statTitle">{title}</p>
        <p className={`statValue ${color}`}>{value}</p>
      </div>
      <div className={`statIcon ${bgColor}`}>
        <Icon style={{ width: "16px", height: "16px", color: "white" }} />
      </div>
    </div>
  </div>
);

const DateFilter = ({ dateFilter, setDateFilter }) => (
  <div className="dateFilterContainer">
    <div className="dateFilter">
      <FaCalendarAlt className="dateFilterIcon" />
      <label className="dateFilterLabel">Select Date Range:</label>
      <DatePicker.RangePicker
        value={[moment(dateFilter.startDate), moment(dateFilter.endDate)]}
        onChange={(dates) => {
          if (dates) {
            setDateFilter({
              startDate: dates[0].format("YYYY-MM-DD"),
              endDate: dates[1].format("YYYY-MM-DD"),
            });
          }
        }}
        style={{ width: 300 }}
        className="dateRangePicker"
      />
    </div>
  </div>
);

const SectionHeader = ({ title, onSeeMore, showSeeMore = true }) => (
  <div className="sectionHeader">
    <h3 className="sectionTitle">{title}</h3>
    {showSeeMore && (
      <button className="seeMoreButton" onClick={onSeeMore}>
        <FaEye className="seeMoreIcon" />
        <span>See More</span>
        <FaArrowRight className="arrowIcon" />
      </button>
    )}
  </div>
);

// Static data
const duesData = [
  {
    customer: "John Doe",
    amount: 1200,
    dueDate: "2024-06-10",
    status: "Pending",
  },
  {
    customer: "Jane Smith",
    amount: 800,
    dueDate: "2024-06-12",
    status: "Paid",
  },
  {
    customer: "Mike Johnson",
    amount: 1500,
    dueDate: "2024-06-15",
    status: "Overdue",
  },
];
const toGetData = [
  { customer: "Acme Corp", amount: 1500, expectedDate: "2024-06-15" },
  { customer: "Beta Ltd", amount: 900, expectedDate: "2024-06-18" },
  { customer: "Gamma Inc", amount: 750, expectedDate: "2024-06-20" },
];
const toGiveData = [
  { vendor: "Logistics Inc", amount: 700, dueDate: "2024-06-11" },
  { vendor: "Supply Co", amount: 400, dueDate: "2024-06-13" },
  { vendor: "Tech Solutions", amount: 850, dueDate: "2024-06-16" },
];
const accountTasks = [
  {
    task: "Reconcile Invoice #123",
    assignedBy: "Manager",
    deadline: "2024-06-10",
    status: "Pending",
  },
  {
    task: "Approve Payment #456",
    assignedBy: "Supervisor",
    deadline: "2024-06-12",
    status: "Pending",
  },
];

const AccountDashboard = () => {
  const history = useHistory();
  const [dateFilter, setDateFilter] = useState({
    startDate: moment().format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
  });

  // Navigation handlers
  const handleDuesSeeMore = () => {
    history.push("/auth/Accounts");
  };
  const handleToGetSeeMore = () => {
    history.push("/auth/Accounts");
  };
  const handleToGiveSeeMore = () => {
    history.push("/auth/Accounts");
  };
  const handleTasksSeeMore = () => {
    history.push("/auth/Accounts");
  };

  // Columns for Outstanding Dues Table
  const duesColumns = [
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      render: (text) => (
        <div className="cellWithIcon">
          <FaUser className="icon" />
          <span style={{ fontWeight: "500", color: "#111827" }}>{text}</span>
        </div>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => (
        <span style={{ fontWeight: "600", color: "#111827" }}>
          ₹{amount.toLocaleString()}
        </span>
      ),
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
      render: (text) => <span style={{ color: "#6b7280" }}>{text}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span className={`statusBadge ${getStatusStyle(status)}`}>
          {status}
        </span>
      ),
    },
  ];

  // Columns for Amount to Receive Table
  const toGetColumns = [
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      render: (text) => (
        <div className="cellWithIcon">
          <FaBuilding className="icon" />
          <span style={{ fontWeight: "500", color: "#111827" }}>{text}</span>
        </div>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => (
        <span style={{ fontWeight: "600", color: "#16a34a" }}>
          ₹{amount.toLocaleString()}
        </span>
      ),
    },
    {
      title: "Expected Date",
      dataIndex: "expectedDate",
      key: "expectedDate",
      render: (text) => <span style={{ color: "#6b7280" }}>{text}</span>,
    },
  ];

  // Columns for Amount to Pay Table
  const toGiveColumns = [
    {
      title: "Vendor",
      dataIndex: "vendor",
      key: "vendor",
      render: (text) => (
        <div className="cellWithIcon">
          <MdAccountBalance className="icon" />
          <span style={{ fontWeight: "500", color: "#111827" }}>{text}</span>
        </div>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => (
        <span style={{ fontWeight: "600", color: "#2563eb" }}>
          ₹{amount.toLocaleString()}
        </span>
      ),
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
      render: (text) => <span style={{ color: "#6b7280" }}>{text}</span>,
    },
  ];

  // Columns for Pending Tasks Table
  const tasksColumns = [
    {
      title: "Task",
      dataIndex: "task",
      key: "task",
      render: (text) => (
        <div className="cellWithIcon">
          <IoMdAlert className="icon" />
          <span style={{ fontWeight: "500", color: "#111827" }}>{text}</span>
        </div>
      ),
    },
    {
      title: "Assigned By",
      dataIndex: "assignedBy",
      key: "assignedBy",
      render: (text) => <span style={{ color: "#6b7280" }}>{text}</span>,
    },
    {
      title: "Deadline",
      dataIndex: "deadline",
      key: "deadline",
      render: (text) => <span style={{ color: "#6b7280" }}>{text}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span className={`statusBadge ${getStatusStyle(status)}`}>
          {status}
        </span>
      ),
    },
  ];

  return (
    <div className="dashboardContainer">
      <div className="dashboardHeader">
        <h1 className="dashboardTitle">Accounts Dashboard</h1>
        <DateFilter dateFilter={dateFilter} setDateFilter={setDateFilter} />
      </div>

      {/* Accounts Statistics */}
      <div className="statsSection">
        <h2 className="statsTitle">Accounts Overview</h2>
        <div className="statsGrid">
          <StatCard
            title="Total Dues"
            value="₹3,500"
            icon={FaDollarSign}
            color="orange"
            bgColor="bgOrange"
            onClick={() => history.push("/auth/Accounts")}
          />
          <StatCard
            title="To Receive"
            value="₹3,150"
            icon={FaArrowUp}
            color="green"
            bgColor="bgGreen"
            onClick={() => history.push("/auth/Accounts")}
          />
          <StatCard
            title="To Pay"
            value="₹1,950"
            icon={FaArrowDown}
            color="blue"
            bgColor="bgBlue"
            onClick={() => history.push("/auth/Accounts")}
          />
          <StatCard
            title="Pending Tasks"
            value={accountTasks.length}
            icon={FaClock}
            color="amber"
            bgColor="bgAmber"
            onClick={() => history.push("/auth/Accounts")}
          />
        </div>
      </div>

      {/* Outstanding Dues Section */}
      <div className="tableSection">
        <SectionHeader title="Outstanding Dues" onSeeMore={handleDuesSeeMore} />
        <div className="tableContainer">
          <Table
            columns={duesColumns}
            dataSource={duesData}
            rowKey={(record, index) => `dues-${index}`}
            pagination={false}
            rowClassName="tableRow"
            className="dashboardTable"
          />
        </div>
      </div>

      {/* Amount to Receive Section */}
      <div className="tableSection">
        <SectionHeader
          title="Amount to Receive"
          onSeeMore={handleToGetSeeMore}
        />
        <div className="tableContainer">
          <Table
            columns={toGetColumns}
            dataSource={toGetData}
            rowKey={(record, index) => `toGet-${index}`}
            pagination={false}
            rowClassName="tableRow"
            className="dashboardTable"
          />
        </div>
      </div>

      {/* Amount to Pay Section */}
      <div className="tableSection">
        <SectionHeader title="Amount to Pay" onSeeMore={handleToGiveSeeMore} />
        <div className="tableContainer">
          <Table
            columns={toGiveColumns}
            dataSource={toGiveData}
            rowKey={(record, index) => `toGive-${index}`}
            pagination={false}
            rowClassName="tableRow"
            className="dashboardTable"
          />
        </div>
      </div>

      {/* Pending Tasks Section */}
      <div className="tableSection">
        <SectionHeader title="Pending Tasks" onSeeMore={handleTasksSeeMore} />
        <div className="tableContainer">
          <Table
            columns={tasksColumns}
            dataSource={accountTasks}
            rowKey={(record, index) => `task-${index}`}
            pagination={false}
            rowClassName="tableRow"
            className="dashboardTable"
          />
        </div>
      </div>
    </div>
  );
};

export default AccountDashboard;
